
import { useState, useEffect } from 'react';
import { useAuth } from '@/components/Auth/AuthProvider';
import { supabase } from '@/integrations/supabase/client';

interface AdminUser {
  id: string;
  is_super_admin: boolean;
  role: string;
  permissions: any;
}

export const useSuperAdminAccess = () => {
  const { user } = useAuth();
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [adminUser, setAdminUser] = useState<AdminUser | null>(null);

  useEffect(() => {
    const checkSuperAdminAccess = async () => {
      if (!user) {
        setIsSuperAdmin(false);
        setLoading(false);
        return;
      }

      try {
        const { data, error } = await supabase
          .from('admin_users')
          .select('*')
          .eq('user_id', user.id)
          .eq('is_super_admin', true)
          .single();

        if (error && error.code !== 'PGRST116') {
          console.error('Error checking super admin access:', error);
          setIsSuperAdmin(false);
        } else if (data) {
          setIsSuperAdmin(true);
          setAdminUser(data);
        } else {
          setIsSuperAdmin(false);
        }
      } catch (error) {
        console.error('Error checking super admin access:', error);
        setIsSuperAdmin(false);
      } finally {
        setLoading(false);
      }
    };

    checkSuperAdminAccess();
  }, [user]);

  const grantSuperAdminAccess = async (email: string) => {
    try {
      if (!user) {
        throw new Error('No authenticated user');
      }

      // Create new super admin user with user_id
      const { error } = await supabase
        .from('admin_users')
        .insert({
          email,
          user_id: user.id,
          role: 'super_admin',
          is_super_admin: true,
          permissions: {
            full_access: true,
            user_management: true,
            billing_management: true,
            system_configuration: true,
            user_signup: true,
            corporate_access: true
          }
        });
      
      if (error) throw error;

      // Refresh the admin status
      setIsSuperAdmin(true);
      setAdminUser({
        id: user.id,
        is_super_admin: true,
        role: 'super_admin',
        permissions: {
          full_access: true,
          user_management: true,
          billing_management: true,
          system_configuration: true,
          user_signup: true,
          corporate_access: true
        }
      });

      return { success: true };
    } catch (error) {
      console.error('Error granting super admin access:', error);
      return { success: false, error };
    }
  };

  const signUpNewUser = async (userData: {
    email: string;
    password: string;
    full_name?: string;
    role?: string;
    plan?: string;
  }) => {
    try {
      // Only super admins can sign up new users
      if (!isSuperAdmin) {
        throw new Error('Unauthorized: Only super admins can sign up new users');
      }

      const { data, error } = await supabase.auth.admin.createUser({
        email: userData.email,
        password: userData.password,
        user_metadata: {
          full_name: userData.full_name,
          role: userData.role || 'user',
          plan: userData.plan || 'basic'
        },
        email_confirm: true
      });

      if (error) throw error;

      // Create admin user record if needed
      if (userData.role && ['admin', 'moderator'].includes(userData.role)) {
        await supabase.from('admin_users').insert({
          email: userData.email,
          user_id: data.user.id,
          role: userData.role,
          is_super_admin: false,
          permissions: {
            user_management: userData.role === 'admin',
            billing_management: false,
            system_configuration: false,
            data_management: true,
            report_generation: true,
            analytics_view: true
          }
        });
      }

      return { success: true, user: data.user };
    } catch (error) {
      console.error('Error signing up new user:', error);
      return { success: false, error };
    }
  };

  return {
    isSuperAdmin,
    loading,
    adminUser,
    grantSuperAdminAccess,
    signUpNewUser
  };
};
